import { usePaginatedQuery } from '@nohara-dxdev/react-core';
import { useScopeIds } from '@nohara-dxdev/react-oidc';
import { useInfiniteQuery } from '@tanstack/react-query';

import { api } from '@/lib/api-client';

import { convertItemFilterToApiParams } from '../utils/convert-filter-to-api-params';

import type { ItemFilterValues } from '../providers';
import type { ItemEntity } from '@/types/api';
import type { ItemRelationshipEntityType } from '@nohara-dxdev/mt-common';
import type { ApiResponse } from '@nohara-dxdev/oidc-core';
import type {
  PaginatedQueryConfig,
  PaginationParams,
  PaginationResponse,
  UsePaginatedQueryOptions,
} from '@nohara-dxdev/react-core';

export const getItems = async (
  orgId: string,
  projectId: string,
  queryParams: ItemFilterValues &
    PaginationParams & { relationships?: ItemRelationshipEntityType[] },
): Promise<PaginationResponse<ItemEntity>> => {
  // Extract pagination params

  const { limit, cursor, relationships, ...filterValues } = queryParams;
  // Convert filter values to API params
  const filterParams = convertItemFilterToApiParams(filterValues);

  const { data } = await api.get<undefined, ApiResponse<PaginationResponse<ItemEntity>>>(
    `/orgs/${orgId}/projects/${projectId}/items`,
    {
      params: {
        ...filterParams,
        relationships,
        limit,
        cursor,
      },
    },
  );

  return data;
};

export const getItemsQueryKey = (
  orgId: string | undefined | null,
  projectId: string | undefined | null,
  params: ItemFilterValues = {},
) => {
  const queryKey: unknown[] = ['items'];
  if (orgId) queryKey.push('org', orgId);
  if (projectId) queryKey.push('project', projectId);
  queryKey.push(params);
  return queryKey;
};

export const getItemsQueryOptions = (
  orgId: string | undefined | null,
  projectId: string | undefined | null,
  params?: ItemFilterValues,
): UsePaginatedQueryOptions<ItemEntity> => {
  return {
    queryKey: getItemsQueryKey(orgId, projectId, params),
    queryFn: ({ pageParam }) =>
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      getItems(orgId!, projectId!, {
        ...params,
        ...pageParam,
      }),
    enabled: !!orgId && !!projectId,
  };
};

type UseItemsOptions = {
  queryConfig?: PaginatedQueryConfig<typeof getItemsQueryOptions>;
} & ItemFilterValues & { relationships?: ItemRelationshipEntityType[] };

export const useItems = ({ queryConfig, ...params }: UseItemsOptions = {}) => {
  const { orgId, projectId } = useScopeIds();
  return usePaginatedQuery({
    ...getItemsQueryOptions(orgId, projectId, params),
    ...queryConfig,
  });
};

export const useItemSelectorItems = (enabled: boolean) => {
  const { orgId, projectId } = useScopeIds();

  return useInfiniteQuery({
    queryKey: [...getItemsQueryKey(orgId, projectId), 'selector'],
    queryFn: ({ pageParam }) => {
      if (!orgId || !projectId) throw new Error('Organization and project are required');
      return getItems(orgId, projectId, pageParam);
    },
    initialPageParam: { limit: 100, cursor: undefined as string | undefined },
    getNextPageParam: (lastPage) =>
      lastPage.cursor ? { limit: 100, cursor: lastPage.cursor } : undefined,
    enabled: enabled && !!orgId && !!projectId,
  });
};
